import type { LoadingValue, Row, Table } from "@glide/computation-model-types";
import {
    asMaybeDate,
    asMaybeNumber,
    asMaybeString,
    isBound,
    isLoadingValue,
    RollupKind,
} from "@glide/computation-model-types";
import {
    ArrayScreenFormat,
    getArrayProperty,
    getEnumProperty,
    getStringProperty,
    getColumnProperty,
    getSwitchProperty,
    getSourceColumnProperty,
    getContentTransforms,
} from "@glide/app-description";
import type {
    DataPlotRowData,
    WireDataPlotComponent,
    WireDataPlotDataPoint,
} from "@glide/fluent-components/dist/js/base-components";
import {
    ChartingGradientMapping,
    DataPlotColorMode,
    type DataPlotDataPointDescription,
    DataPlot,
    ChartingColorScheme,
    DataPlotRollupKind,
} from "@glide/fluent-components/dist/js/fluent-components";
import { getTableAndColumnForSourceColumn, type InlineListComponentDescription } from "@glide/function-utils";
import type { WireAlwaysEditableValue, WireInflationBackend, WireRowComponentHydrationBackend } from "@glide/wire";
import { DataPlotChartType, DataPlotType, type WireRowHydrationValueProvider, WireComponentKind } from "@glide/wire";
import {
    getFormatForInputColumn,
    inflateComponentEnricher,
    inflateDateTimeProperty,
    inflateStringProperty,
    makeSimpleWireTableComponentHydratorConstructor,
} from "../../wire/utils";
import { makeFluentArrayContentHandler } from "../fluent-array-handler";
import { assertNever, definedMap, mapFilterUndefined } from "@glideapps/ts-necessities";
import type { FilterCondition, TableColumn } from "@glide/type-schema";
import {
    isBigTableOrExternal,
    isPrimitiveType,
    isDateTimeTypeKind,
    BinaryPredicateCompositeOperator,
    getTableColumnDisplayName,
    maybeGetTableColumn,
} from "@glide/type-schema";
import { isEmpty } from "@glide/support";
import { isExperimentEnabled } from "@glide/common-core/dist/js/use-feature-settings";
import { decomposeAggregateRow } from "../../wire/aggregates";
import { GlideDateTime, GlideJSON } from "@glide/data-types";
import type { TimeSeriesRangeGlideJSON } from "./data-plot-time-series-helpers";
import {
    convertTimeDurationToBucket,
    convertTimeSeriesRangeToDate,
    createTimeSeriesAggregationInBuckets,
    getDefaultIntervalFromDuration,
    getIntervalFromDateRange,
    isTimeSeriesRange,
    makeTimeBucketsFromDurationAndInterval,
} from "./data-plot-time-series-helpers";
import { isColumnAllowedForFilteringRows } from "../../allowed-columns";
import { getLocalizedString } from "@glide/localization";
import { getSortFromArrayTransforms } from "../../description-utils";
import { TimeSeriesInterval, TimeSeriesRelativeDuration } from "@glide/component-utils";
import { ValueFormatKind } from "@glide/formula-specifications";

function convertDataPlotRollupKindToRollupKind(rollupKind: DataPlotRollupKind): RollupKind {
    switch (rollupKind) {
        case DataPlotRollupKind.Sum:
            return RollupKind.Sum;
        case DataPlotRollupKind.Average:
            return RollupKind.Average;
        case DataPlotRollupKind.Minimum:
            return RollupKind.Minimum;
        case DataPlotRollupKind.Maximum:
            return RollupKind.Maximum;
        case DataPlotRollupKind.CountNonEmpty:
            return RollupKind.CountNonEmpty;
        default:
            assertNever(rollupKind);
    }
}

function isTimeSeriesRelativeDurationOrShowAllOrTimeSeriesRangeGlideJSON(
    value: unknown
): value is TimeSeriesRelativeDuration | TimeSeriesRangeGlideJSON | "All" {
    switch (value) {
        case TimeSeriesRelativeDuration.Last24Hours:
        case TimeSeriesRelativeDuration.Last7Days:
        case TimeSeriesRelativeDuration.Last30Days:
        case TimeSeriesRelativeDuration.Last90Days:
        case TimeSeriesRelativeDuration.Last12Months:
        case TimeSeriesRelativeDuration.Yesterday:
        case TimeSeriesRelativeDuration.LastWeek:
        case TimeSeriesRelativeDuration.LastMonth:
        case TimeSeriesRelativeDuration.LastQuarter:
        case TimeSeriesRelativeDuration.LastYear:
        case TimeSeriesRelativeDuration.DayToNow:
        case TimeSeriesRelativeDuration.WeekToDate:
        case TimeSeriesRelativeDuration.MonthToDate:
        case TimeSeriesRelativeDuration.QuarterToDate:
        case TimeSeriesRelativeDuration.YearToDate:
        case "All":
            return true;
    }

    if (value instanceof GlideJSON && isTimeSeriesRange(value.jsonValue)) {
        return true;
    }

    return false;
}

function isTimeSeriesInterval(value: unknown): value is TimeSeriesInterval {
    switch (value) {
        case TimeSeriesInterval.Hour:
        case TimeSeriesInterval.Day:
        case TimeSeriesInterval.Week:
        case TimeSeriesInterval.Month:
        case TimeSeriesInterval.Year:
        case TimeSeriesInterval.Quarter:
            return true;
    }

    return false;
}

function getDataPlotFromChartType(chartType: Omit<DataPlotChartType, DataPlotChartType.Mixed>): {
    type: DataPlotType;
    stack: string | undefined;
} {
    switch (chartType) {
        case DataPlotChartType.Line:
            return { type: DataPlotType.Line, stack: undefined };
        case DataPlotChartType.Bar:
            return { type: DataPlotType.Bar, stack: undefined };
        case DataPlotChartType.StackedBar:
            return { type: DataPlotType.Bar, stack: "STACK-BAR-ID" };
        case DataPlotChartType.Area:
            return { type: DataPlotType.Area, stack: undefined };
        case DataPlotChartType.StackedArea:
            return { type: DataPlotType.Area, stack: "STACK-AREA-ID" };
        case DataPlotChartType.Scatter:
            return { type: DataPlotType.Scatter, stack: undefined };
        default:
            return { type: DataPlotType.Bar, stack: undefined };
    }
}

function canQueryByColumn(column: TableColumn, ib: WireInflationBackend): boolean {
    const gbtComputedColumnsAlpha = isExperimentEnabled("gbtComputedColumnsAlpha", ib.adc.userFeatures);
    const gbtDeepLookups = isExperimentEnabled("gbtDeepLookups", ib.adc.userFeatures);

    if (isBigTableOrExternal(ib.tables.input)) {
        return isColumnAllowedForFilteringRows(
            true,
            true,
            ib.adc,
            ib.tables.input,
            column,
            undefined, // We don't need the computation model for queryables
            gbtComputedColumnsAlpha,
            gbtDeepLookups
        );
    }

    return isPrimitiveType(column.type) && column.type.kind !== "json";
}

export const dataPlotFluentArrayContentHandler = makeFluentArrayContentHandler(
    DataPlot,
    (ib, desc, containingRowIB) => {
        if (containingRowIB === undefined) {
            return undefined;
        }

        const [componentTitleGetter] = inflateStringProperty(containingRowIB, desc.title, true);
        const dataPoints = getArrayProperty<DataPlotDataPointDescription>(desc.points) ?? [];

        const undefinedColorGetter = () => undefined;
        const dataPointColorValueGetters = dataPoints.map(p => {
            const colorMode = getEnumProperty<DataPlotColorMode>(p.colorMode);
            if (colorMode === DataPlotColorMode.Auto) {
                return [undefinedColorGetter];
            }
            return inflateStringProperty(ib, p.color, true);
        });

        const chartType = getEnumProperty<DataPlotChartType>(desc.chartType) ?? DataPlotChartType.Mixed;

        // One way we enable dynamic colors is by using a column as the color property.
        // But, if this is the case we need to make sure we don't show the legend since
        // it is possible multiple colors can be resolved from this getter.
        let hasColumnBasedColorBinding = false;
        const columnsToDisplay = mapFilterUndefined(dataPoints, (p, i) => {
            const columnName = getColumnProperty(p.value);
            const dataKeyColumn = definedMap(getSourceColumnProperty(p.value), sc =>
                getTableAndColumnForSourceColumn(ib.adc, sc, ib.tables.input, undefined)
            )?.column;
            const colorMode = getEnumProperty<DataPlotColorMode>(p.colorMode);
            const color = getStringProperty(p.color);
            const colorColumn = getSourceColumnProperty(p.color);
            if (colorColumn !== undefined && colorMode === DataPlotColorMode.Manual) {
                hasColumnBasedColorBinding = true;
            }
            if (columnName === undefined || dataKeyColumn === undefined) {
                return undefined;
            }

            // The data key must be unique for each data point, even if
            // they're using the same column.
            const dataKey = `${columnName}-${i}`;
            const caption = getStringProperty(p.caption);
            const nonEmptyCaption = !isEmpty(caption) ? caption : undefined;
            const label = nonEmptyCaption ?? getTableColumnDisplayName(dataKeyColumn);
            const columnType = dataKeyColumn.type;
            const rollupKind =
                columnType.kind === "number"
                    ? getEnumProperty<DataPlotRollupKind>(p.rollupKind)
                    : DataPlotRollupKind.CountNonEmpty;

            const { type, stack } =
                chartType === DataPlotChartType.Mixed
                    ? {
                          type: getEnumProperty<DataPlotType>(p.displayType) ?? DataPlotType.Bar,
                          stack: getStringProperty(p.stack),
                      }
                    : getDataPlotFromChartType(chartType);

            const format = getFormatForInputColumn(ib, dataKeyColumn.name);

            // Format specification handles all formatting including currency symbols
            const result: WireDataPlotDataPoint = {
                label,
                dataKey,
                color: isEmpty(color) || colorMode === DataPlotColorMode.Auto ? undefined : color,
                type,
                rollupKind,
                stack,
                format: format?.kind === ValueFormatKind.Number ? format : undefined,
            };
            return result;
        });

        const [labelGetter] = inflateStringProperty(ib, desc.label, true);
        const [labelAsTimeGetter] = inflateDateTimeProperty(ib, desc.label);
        const labelAxisColumnName = getColumnProperty(desc.label);
        const labelAxisColumn = maybeGetTableColumn(ib.tables.input, labelAxisColumnName);
        const labelAxisFormat = getFormatForInputColumn(ib, labelAxisColumnName);
        const isDateXAxis = labelAxisColumn !== undefined && isDateTimeTypeKind(labelAxisColumn.type.kind);

        const emptyLabelValue = getLocalizedString("chartsNoLabelValue");

        const colorScheme = getEnumProperty<ChartingColorScheme>(desc.colorScheme) ?? ChartingColorScheme.Default;
        const gradientColorProperties = (getArrayProperty<{ color?: string }>(desc.gradientColors) ?? []).map(g =>
            getStringProperty(g.color)
        );
        const gradientColors = mapFilterUndefined(gradientColorProperties, c => (!isEmpty(c) ? c : undefined));
        const gradientMapping =
            dataPoints.length === 1
                ? getEnumProperty<ChartingGradientMapping>(desc.gradientMapping)
                : ChartingGradientMapping.Index;

        const initialTimeRange =
            getEnumProperty<TimeSeriesRelativeDuration | "All">(desc.timeSeriesRelativeDuration) ??
            TimeSeriesRelativeDuration.Last30Days;

        const componentEnricher = inflateComponentEnricher<WireDataPlotComponent>(
            ib,
            desc as unknown as InlineListComponentDescription
        );

        const timeSeriesRelativeDuration =
            getEnumProperty<TimeSeriesRelativeDuration | "All">(desc.timeSeriesRelativeDuration) ?? "All";
        const timeSeriesInterval = getEnumProperty<TimeSeriesInterval | "Auto">(desc.timeSeriesInterval) ?? "Auto";

        const enableTimeFilters = isDateXAxis && (getSwitchProperty(desc.enableTimeFilters) ?? false);

        const canShowLegend =
            !(colorScheme === ChartingColorScheme.Gradient && gradientMapping === ChartingGradientMapping.Data) &&
            !hasColumnBasedColorBinding;

        const timeRangeOptions = (
            getArrayProperty<{ value: TimeSeriesRelativeDuration }>(desc.configurableTimeRangeOptions) ?? []
        ).map(({ value }) => value);

        function makeComponent(
            containingRowHB: WireRowHydrationValueProvider,
            data: DataPlotRowData[],
            timeSeriesRelativeDurationState: WireAlwaysEditableValue<
                "All" | TimeSeriesRelativeDuration | TimeSeriesRangeGlideJSON
            >,
            table: Table | LoadingValue | undefined,
            timeSeriesIntervalState: WireAlwaysEditableValue<TimeSeriesInterval | undefined>,
            timeSeriesEffectiveInterval: TimeSeriesInterval | undefined
        ) {
            const graphTitle = componentTitleGetter(containingRowHB);
            const component = componentEnricher({
                kind: WireComponentKind.List,
                format: ArrayScreenFormat.DataPlot,
                data,
                columnsToDisplay,
                graphTitle,
                showYAxisLabels: getSwitchProperty(desc.showYAxisLabels),
                showLegend: canShowLegend && getSwitchProperty(desc.showLegend),
                colorScheme,
                gradientColors,
                gradientMapping,
                initialTimeRange,
                timeSeriesRelativeDurationState,
                enableTimeFilters,
                isLoading: isLoadingValue(table),
                timeSeriesIntervalState,
                timeSeriesEffectiveInterval,
                timeRangeOptions,
            });

            return { component, isValid: true };
        }

        function makeChartDataFromRow(rhb: WireRowComponentHydrationBackend, row: Row): DataPlotRowData | undefined {
            if (dataPoints === undefined || dataPoints.length === 0) {
                return undefined;
            }

            const labelName = labelGetter(rhb);
            const maybeLabelAsDate = timeSeriesRelativeDuration === "All" ? undefined : labelAsTimeGetter(rhb);
            // FIXME: I guess we have to deal with timezones here probably.
            const labelTimestamp = isBound(maybeLabelAsDate) ? maybeLabelAsDate?.getUTCValue() : undefined;

            if (!isBound(labelName)) {
                return undefined;
            }

            const chartRowData: DataPlotRowData = {
                name: isEmpty(labelName) ? emptyLabelValue : labelName,
                timestamp: labelTimestamp,
                points: {},
            };

            // if the row has a group property, we need to decompose it to get the grouped labels.
            // fortunately, the values we expect get set at correct data key via withGroupBy
            if (row.hasOwnProperty("group")) {
                const { group } = decomposeAggregateRow(row);
                const stringGroup = asMaybeString(group);
                const dateGroup = asMaybeDate(group);
                if (stringGroup !== undefined && !isEmpty(stringGroup)) {
                    chartRowData.name = stringGroup;
                }
                if (dateGroup !== undefined) {
                    chartRowData.timestamp = dateGroup.getUTCValue();
                }
            }

            dataPoints.forEach((_, index) => {
                const [colorGetter] = dataPointColorValueGetters[index];
                const color = colorGetter(rhb);
                const dataKey = columnsToDisplay[index]?.dataKey;

                if (dataKey === undefined) return;

                const value = asMaybeNumber(row[dataKey]);
                if (value === undefined) return;

                chartRowData.points[dataKey] = {
                    value,
                    color: isBound(color) ? color : undefined,
                };
            });
            return chartRowData;
        }

        // We use these to show data points on the chart when loading new data
        let prevRows: readonly Row[] = [];

        return makeSimpleWireTableComponentHydratorConstructor(
            ib,
            () => {
                // noop 🎉 see withOnlyQueries in fluent spec.
                return undefined;
            },
            (rhb, query) => {
                if (labelAxisColumn === undefined) {
                    return undefined;
                }

                const timeSeriesRelativeDurationState = rhb.getState<
                    TimeSeriesRelativeDuration | "All" | TimeSeriesRangeGlideJSON
                >(
                    "timeSeriesRelativeDurationState",
                    isTimeSeriesRelativeDurationOrShowAllOrTimeSeriesRangeGlideJSON,
                    timeSeriesRelativeDuration,
                    true
                );

                const timeSeriesIntervalState = rhb.getState<TimeSeriesInterval | undefined>(
                    "timeSeriesIntervalState",
                    isTimeSeriesInterval,
                    timeSeriesInterval === "Auto" ? undefined : timeSeriesInterval,
                    true
                );

                const timeSeriesRelativeDurationValue = enableTimeFilters
                    ? timeSeriesRelativeDurationState.value
                    : timeSeriesRelativeDuration;

                const timeSeriesIntervalValue = enableTimeFilters
                    ? timeSeriesIntervalState.value
                    : timeSeriesInterval === "Auto"
                    ? undefined
                    : timeSeriesInterval;

                const maybeSort = getSortFromArrayTransforms(getContentTransforms(desc));
                query = query.withGroupBy({
                    columns: [labelAxisColumn.name],
                    aggregates: mapFilterUndefined(dataPoints, (dataPoint, i) => {
                        const columnName = getColumnProperty(dataPoint.value);
                        const column = maybeGetTableColumn(ib.tables.input, columnName);
                        const columnFromDisplay = columnsToDisplay[i];

                        if (
                            column === undefined ||
                            columnName === undefined ||
                            columnFromDisplay?.rollupKind === undefined
                        ) {
                            return undefined;
                        }

                        const rollupKind = columnFromDisplay.rollupKind;
                        const columnIsQueryable = canQueryByColumn(column, ib);

                        if (!columnIsQueryable) {
                            return undefined;
                        }

                        return {
                            column: columnName,
                            kind: convertDataPlotRollupKindToRollupKind(rollupKind),
                            name: columnFromDisplay.dataKey,
                        };
                    }),
                    sort: maybeSort === undefined ? [{ columnName: labelAxisColumn.name, order: "asc" }] : [maybeSort],
                    // since we always aggregate we can make this fairly high number of rows
                    limit: 4_000,
                });

                if (isDateXAxis && timeSeriesRelativeDurationValue !== "All") {
                    const { start, end } =
                        timeSeriesRelativeDurationValue instanceof GlideJSON &&
                        isTimeSeriesRange(timeSeriesRelativeDurationValue.jsonValue)
                            ? convertTimeSeriesRangeToDate(timeSeriesRelativeDurationValue.jsonValue)
                            : convertTimeDurationToBucket(timeSeriesRelativeDurationValue);

                    const afterCondition: FilterCondition = {
                        kind: BinaryPredicateCompositeOperator.IsOnOrAfter,
                        lhs: { columnName: labelAxisColumn.name },
                        rhs: GlideDateTime.fromTimeZoneAwareDate(start).toDocumentData(),
                        negated: false,
                    };
                    const beforeCondition: FilterCondition = {
                        kind: BinaryPredicateCompositeOperator.IsOnOrBefore,
                        lhs: { columnName: labelAxisColumn.name },
                        rhs: GlideDateTime.fromTimeZoneAwareDate(end).toDocumentData(),
                        negated: false,
                    };
                    query = query.withCondition(afterCondition);
                    query = query.withCondition(beforeCondition);
                }

                const table = rhb.resolveQueryAsTable(query);

                if (table === undefined) {
                    return undefined;
                }

                const rows = isLoadingValue(table) ? prevRows : table.asMutatingArray();
                prevRows = rows;

                let chartData: DataPlotRowData[] = [];
                let minTimestamp = 0;
                let maxTimestamp = 0;
                for (const row of rows) {
                    const queryableRhb = rhb?.makeHydrationBackendForRow(row, undefined, ib.tables);
                    if (queryableRhb === undefined) {
                        continue;
                    }
                    const chartDataRow = makeChartDataFromRow(queryableRhb, row);
                    if (chartDataRow !== undefined) {
                        chartData.push(chartDataRow);
                        if (chartDataRow.timestamp !== undefined) {
                            if (minTimestamp === 0 || chartDataRow.timestamp < minTimestamp) {
                                minTimestamp = chartDataRow.timestamp;
                            }
                            if (chartDataRow.timestamp > maxTimestamp) {
                                maxTimestamp = chartDataRow.timestamp;
                            }
                        }
                    }
                }

                let effectiveInterval: TimeSeriesInterval | undefined;

                // Eventually, it would be better to perform this aggregation server side
                // but we will need to support date_trunc or similar in SQL mapping.
                if (isDateXAxis) {
                    const aggregationsMap = new Map<string, DataPlotRollupKind | undefined>();
                    for (const column of columnsToDisplay) {
                        aggregationsMap.set(column.dataKey ?? "", column.rollupKind as DataPlotRollupKind);
                    }

                    // For "All" duration, calculate the smart interval based on actual data range
                    // but only if the user hasn't explicitly set a different interval
                    effectiveInterval =
                        timeSeriesIntervalValue ?? getDefaultIntervalFromDuration(timeSeriesRelativeDurationValue);

                    if (
                        timeSeriesIntervalValue === undefined &&
                        timeSeriesRelativeDurationValue === "All" &&
                        minTimestamp > 0 &&
                        maxTimestamp > minTimestamp
                    ) {
                        effectiveInterval = getIntervalFromDateRange(minTimestamp, maxTimestamp);
                    }

                    // Extract timezone from the X-axis date format, defaulting to "local"
                    const timeZone =
                        labelAxisFormat?.kind === ValueFormatKind.DateTime ? labelAxisFormat.timeZone : "local";

                    chartData = createTimeSeriesAggregationInBuckets(
                        makeTimeBucketsFromDurationAndInterval(
                            timeSeriesRelativeDurationValue,
                            effectiveInterval,
                            minTimestamp,
                            maxTimestamp,
                            timeZone
                        ),
                        chartData,
                        aggregationsMap
                    );
                }

                return makeComponent(
                    rhb,
                    chartData,
                    timeSeriesRelativeDurationState,
                    table,
                    timeSeriesIntervalState,
                    effectiveInterval
                );
            }
        );
    }
);
